import { Link } from "react-router";

import { DateTimePicker } from "@/components/date-time-picker";
import { Field } from "@/components/form-field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { PatchChallengeInput } from "@/lib/api";

import { SectionCard } from "./section-card";
import { useChallenge, useChallengeState } from "./use-challenge";

/**
 * Whether each team hands in a report for this challenge (difficulty, slides PDF, code),
 * by when, and which slide template to fill in.
 *
 * The report deadline is its own field because the report is due after the runs: the
 * syllabus puts the final report the morning after the final challenge closes.
 */
export function AdminChallengeReport() {
  const { challenge, refetch } = useChallenge();
  const [form, set, reset] = useChallengeState((c) => ({
    collects_report: c.collects_report,
    report_deadline: c.report_deadline,
    report_template_url: c.report_template_url,
  }));

  const patch: PatchChallengeInput = {};
  if (form.collects_report !== challenge.collects_report)
    patch.collects_report = form.collects_report;
  if (form.report_deadline !== challenge.report_deadline)
    patch.report_deadline = form.report_deadline;
  if (form.report_template_url.trim() !== challenge.report_template_url)
    patch.report_template_url = form.report_template_url.trim();

  return (
    <SectionCard
      title="Report"
      description={
        <>
          Each team rates the difficulty (1 to 5) and hands in its slides as a PDF and its
          agent&apos;s code (.py or .zip) on the challenge page. Before the deadline a
          team can replace it; after, the version on record is final, and a team with nothing
          on record may still hand in once, marked late. Everything handed in is on{" "}
          <Link to="/admin/reports" className="text-foreground underline underline-offset-4">
            Reports
          </Link>
          .
        </>
      }
      patch={patch}
      onSaved={refetch}
      onReset={reset}
    >
      <div className="flex items-center justify-between gap-4 rounded-md border p-3">
        <div className="grid gap-1">
          <Label htmlFor="rp-collects">Collect a report</Label>
          <p className="text-muted-foreground text-xs">
            Shows the report card to students of this course. Turning it off hides the card and
            keeps whatever was handed in.
          </p>
        </div>
        <Switch
          id="rp-collects"
          checked={form.collects_report}
          onCheckedChange={(checked) => set((f) => ({ ...f, collects_report: checked }))}
        />
      </div>

      <Field
        id="rp-deadline"
        label="Report deadline"
        hint="Leave blank for none. Separate from the challenge's own deadline."
      >
        <DateTimePicker
          id="rp-deadline"
          value={form.report_deadline}
          onChange={(report_deadline) => set((f) => ({ ...f, report_deadline }))}
          className="w-full sm:w-80"
        />
      </Field>

      <Field
        id="rp-template"
        label="Report template"
        hint="A link to the slide deck teams copy and fill in. Shown on the report card."
      >
        <Input
          id="rp-template"
          type="url"
          placeholder="https://docs.google.com/presentation/d/…"
          value={form.report_template_url}
          onChange={(e) => set((f) => ({ ...f, report_template_url: e.target.value }))}
        />
      </Field>
    </SectionCard>
  );
}
